import { ReactElement, useState, useEffect } from 'react';
import _ from 'lodash';
import { BigNumber, BigNumberish, ContractTransaction, ethers } from 'ethers';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import SandboxPoster from '@/framework/events/SandboxPoster';
import { ContextTokens } from '@/services/reducers/token';
import { WalletContext } from '@/services';

import { signMessage as wagmiSignMessage } from '@web3-onboard/wagmi';
/**
 * 合并class
 * @param classeNames
 * @returns
 */
export const mergeClasses = (...classeNames: string[]): string => {
    const _classNames: string[] = [];
    classeNames.filter((o) => !_.isEmpty(o)).forEach((o) => _classNames.push(o));
    return _classNames.join(' ');
};

/**
 * 遍历生成 react
 * @param array 数组
 * @param consumer  生成方式
 * @returns  react 元素
 */
export const iteratorTos = <T>(array: T[], consumer: (o: T, i: number) => ReactElement | undefined): ReactElement[] => {
    if (_.isEmpty(array)) {
        return [];
    }
    const _list: ReactElement[] = [];
    array.forEach((o, i) => {
        const _elt: ReactElement | undefined = consumer(o, i);
        if (_elt != undefined) {
            _list.push(_elt);
        }
    });
    return _list;
};

/**
 * 生成一个包装器
 * @param array array
 * @param wrapper  wrapper
 * @param consumer  消费者
 * @returns  结果
 */
export const iteratorTo = <T>(array: T[], wrapper: (children: ReactElement[]) => ReactElement, consumer: (o: T, i: number) => ReactElement | undefined): ReactElement => {
    return wrapper(iteratorTos(array, consumer));
};

export const iteratorNumTo = (num: number, wrapper: (children: ReactElement[]) => ReactElement, consumer: (o: number, i: number) => ReactElement | undefined): ReactElement => {
    const array = [] as number[];
    for (let i = 0; i < num; i++) {
        array.push(i);
    }
    return wrapper(iteratorTos<number>(array, consumer));
};

export const iteratorNumTos = (num: number, consumer: (o: number, i: number) => ReactElement | undefined): ReactElement[] => {
    const array = [] as number[];
    for (let i = 0; i < num; i++) {
        array.push(i);
    }
    return iteratorTos<number>(array, consumer);
};

/**
 * 替换 class
 * @param body  body元素
 * @param className 类名
 * @param removeClasses 移除的类名
 */
export const addClassName = (body: HTMLElement, className: string, removeClasses: string[] = []): void => {
    const __classList = body.classList;
    // 移除已经存在的class
    removeClasses.forEach((o) => __classList.remove(o));
    // 解析 classname
    _.isEmpty(className) || __classList.add(className);
};

/**
 * addres sensitive
 * @param addr
 * @returns
 */
export const sensitiveAddress = (addr: string | undefined | null): string => {
    if (!addr) {
        return '';
    }
    return addr.substring(0, 6) + '...' + addr.substring(addr.length - 4);
};

/**
 * switch network
 * @param chainId
 */
export const switchNetwork = async (chainId: number): Promise<any> => {
    await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }], // chainId must be in hexadecimal numbers
    });
    return chainId;
};
/**
 * 等待签名
 * @param sign 签名函数
 * @returns 结果
 */
export const waitSign = (waitTransaction: Promise<ContractTransaction>): Promise<ContractTransaction> => {
    return new Promise((res, rej) => {
        waitTransaction
            .then((tran) => {
                SandboxPoster.Post('Transaction.Add', tran);
                res(tran);
            })
            .catch(rej);
    });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const handelPromise = <T>(fn: Promise<T>, loadingFn?: any): Promise<T> => {
    if (loadingFn !== undefined) {
        loadingFn(true);
    }
    return new Promise((res, reject) => {
        fn.then((tran) => {
            res(tran);
        })
            .catch((error) => {
                console.error(error);
                const code = 500;
                const message = String(error);
                reject({ code, message });
            })
            .finally(() => {
                if (loadingFn !== undefined) {
                    loadingFn(false);
                }
            });
    });
};

/**
 * BigNumber Mul a * b
 * @param a
 * @param b
 * @returns a*b
 */
export const bigNumberMul = (a: BigNumber, b: BigNumberish): BigNumber => {
    const oneBN: BigNumber = parseUnits('1', 18);
    return a.mul(b).div(oneBN);
};

export const isZeroAddress = (ad?: string): boolean => !ad || ad == ethers.constants.AddressZero;

export const isNumberStr = (str: string): boolean => str != undefined && str.length > 0 && /^[0-9.]+$/.test(str);
/**
 * 显示数字格式化
 * @param v  数据值
 * @param figure  位数
 * @returns
 */
export const fmtBigNumber = (v: string | BigNumber, figure: number): string => {
    const number = Math.pow(10, figure);
    let amount: BigNumber;
    if (BigNumber.isBigNumber(v)) {
        amount = v;
    } else {
        amount = parseUnits(v);
    }
    const result = Math.floor(parseInt(formatUnits(amount.mul(BigNumber.from(number))))) / number;
    return result.toString();
};

/**
 * 从上下文中查询token
 * @param name 名称
 * @returns token info
 */
export const findContextToken = (name: string): Erc20TokenInfo | undefined => {
    if (!WalletContext.Chain) {
        return undefined;
    }
    const tokens = ContextTokens[WalletContext.Chain.name];
    if (!tokens) {
        return undefined;
    }
    return tokens[name];
};

export const signMessage = (msg: string): Promise<string> => {
    return new Promise<string>((res, rej) => {
        if (!WalletContext.Provider || !WalletContext.Address || !WalletContext.Onboard || !WalletContext.Chain) {
            rej('UNCONNECTED_ERROR');
            return;
        }
        try {
            const [activeWallet] = WalletContext.Onboard.state.get().wallets;
            const wagmiConfig = WalletContext.Onboard.state.get().wagmiConfig;
            wagmiSignMessage(wagmiConfig, {
                message: msg,
                connector: activeWallet.wagmiConnector,
            })
                .then(res)
                .catch(rej);
        } catch (e) {
            rej(e);
        }
    });
};

export const toFixed = (value: string | BigNumber, precision: number, decimals = 18): string => {
    let amount: BigNumber;
    if (BigNumber.isBigNumber(value)) {
        amount = value;
    } else {
        amount = parseUnits(value);
    }
    const str = parseFloat(formatUnits(amount, decimals)).toFixed(precision);
    return str.replace(/(?:\.0*|(\.\d+?)0+)$/, '$1');
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const formatString = function (..._args: any[]) {
    // eslint-disable-next-line prefer-rest-params
    const args = [].slice.call(arguments, 1);
    let i = 0;
    return _args[0].replace(/%s/g, () => args[i++]);
};

const currencyUnits = ['K', 'M', 'B', 'KB', 'MB', 'BB'];

export const formatCurrency = function (n: number | string): string {
    const value = _.isNumber(n) ? `${n}` : n;
    const idx = value.indexOf('.');
    const im = idx == -1 ? value : value.substring(0, idx);
    // 对输入的数字进行格式化，如果数字太大，则转换为Million或Billion单位
    for (let i = currencyUnits.length; i > 0; i--) {
        const unit = currencyUnits[i - 1];
        const length = i * 3;
        if (im.length <= length) {
            continue;
        }
        const m = im.length - length;
        return im.substring(0, m) + '.' + im.substring(m, m + 2) + unit;
    }
    // 没有小数点
    if (idx == -1) {
        return im;
    }
    const fixedM = value.substring(idx + 1);
    if (fixedM.length <= 2) {
        return im + '.' + fixedM;
    }
    return im + '.' + fixedM.substring(0, 2);
};

export const toFixedFloor = function (num: number, decimal: number): string {
    return (Math.floor(num * Math.pow(10, decimal)) / Math.pow(10, decimal)).toFixed(decimal);
};

export const obfuscatedAddress = function (address: string): string {
    if (!address) {
        return '';
    }
    const obfuscated = `${address.substring(0, 6)}***${address.substring(address.length - 4)}`;
    return obfuscated;
};

/**
 * 请求改轮询(组件销毁会自动停止轮询,调用endPolling也可主动停止轮询)
 * @param cb 要进行轮询的请求,cb函数返回true继续轮询,返回false或throw error停止轮询
 * @param time 轮询间隔时间
 * @returns [startPolling, endPolling] 轮询开始函数，轮询停止函数
 */
export function usePolling(cb: () => Promise<boolean>, time = 2000): [startPolling: () => void, endPolling: () => void] {
    // countTime变化引发轮询,startCount(1)开始轮询,startCount(0)终止轮询
    const [countTime, startCount] = useState<number>(0);
    useEffect(() => {
        if (countTime > 0) {
            setTimeout(() => {
                cb()
                    .then((res) => {
                        if (res)
                            startCount((countTemp) => {
                                return countTemp === 0 ? 0 : countTemp + 1;
                            });
                    })
                    .catch();
            }, time);
        }
    }, [countTime]);
    const startPolling = () => startCount((countTemp) => countTemp + 1);
    const endPolling = () => startCount(0);
    return [startPolling, endPolling];
}

export const sleep = (time: number) => {
    return new Promise((resolve) => setTimeout(resolve, time));
};

export const getDealine = (dealine = 50): number => {
    return Math.floor(new Date().getTime() / 1000) + dealine;
};
/**
 * 等待一段时间，直到 onCheck 结果返回 true
 * @param onCheck 检查结果
 * @param checkInterval 检查间隔，单位毫秒，默认 100
 * @returns
 */
export function waitUntil(onCheck: () => boolean, checkInterval = 100): Promise<void> {
    return new Promise(function (res) {
        const it = setInterval(() => {
            if (!onCheck()) return;
            clearInterval(it);
            res();
        }, checkInterval);
    });
}

/**
 * 把对象序列化为 query string
 * @param params 对象 {a:1, b:'hi'}
 * @param encode 是否对 key/value 进行 encodeURIComponent (默认 true)
 * @returns 例如 "a=1&b=hi"
 */
export function queryStringify(params: Record<string, any>, encode: boolean = true): string {
    if (!params) return '';

    return Object.entries(params)
        .filter(([_, v]) => v !== undefined && v !== null) // 过滤掉 null/undefined
        .map(([k, v]) => {
            if (Array.isArray(v)) {
                return v.map((item) => (encode ? `${encodeURIComponent(k)}=${encodeURIComponent(String(item))}` : `${k}=${String(item)}`)).join('&');
            }
            return encode ? `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}` : `${k}=${String(v)}`;
        })
        .join('&');
}

export const uuid = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
